import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome, TopChromeActionButton } from '../components/TopChrome';
import { useShellNav } from '../components/AppFrame';
import { useTokens } from '../state/theme';
import { guidedLessonRoute } from '../state/lesson-entry';
import {
  lezioneApi,
  type ArgomentoDetailResponse,
  type CanonicalLessonProgressArgomento,
  type CanonicalLessonProgressSummary,
} from '../api/lezione';
import { buildCanonicalLessonRows } from '../state/canonical-learning';
import { useLessonReadProgress, type LessonReadRecord } from '../state/lesson-read-progress';

type Phase =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      data: ArgomentoDetailResponse;
      lessonProgress: CanonicalLessonProgressSummary | null;
    };

export default function ArgomentoDetailScreen(): JSX.Element {
  const { argomentoId = '' } = useParams<{ argomentoId: string }>();
  const navigate = useNavigate();
  const t = useTokens();
  const { goBack } = useShellNav();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const lessonReadByMicroTopic = useLessonReadProgress((s) => s.byMicroTopic);

  const load = useCallback(async (isCancelled: () => boolean = () => false) => {
    if (!argomentoId) {
      setPhase({ kind: 'error', message: 'Argomento non trovato.' });
      return;
    }
    try {
      const [data, lessonProgress] = await Promise.all([
        lezioneApi.argomentoDetail(argomentoId),
        lezioneApi.lessonProgressSummary().catch(() => null),
      ]);
      if (!isCancelled()) setPhase({ kind: 'ready', data, lessonProgress });
    } catch {
      if (!isCancelled()) {
        setPhase({
          kind: 'error',
          message: 'Non sono riuscito a caricare le lezioni. Riprova tra poco.',
        });
      }
    }
  }, [argomentoId]);

  useEffect(() => {
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    const reload = () => { void load(() => cancelled); };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reload();
    };
    window.addEventListener('focus', reload);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', reload);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [load]);

  const subtitle = phase.kind === 'ready'
    ? 'Percorso completato'
    : 'Lezioni';

  return (
    <Screen>
      <TopChrome
        title={phase.kind === 'ready' ? phase.data.argomentoName : 'Argomento'}
        subtitle={subtitle}
        onBack={goBack}
        actions={<TopChromeActionButton onClick={() => navigate('/progresso')}>Progressi</TopChromeActionButton>}
      />
      <main
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px 16px 32px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {phase.kind === 'loading' && (
          <div
            data-testid="argomento-loading"
            style={{
              padding: '32px 16px',
              textAlign: 'center',
              fontFamily: t.fonts.body,
              fontSize: 14,
              color: t.textMuted,
              lineHeight: 1.5,
            }}
          >
            Sto caricando le lezioni...
          </div>
        )}

        {phase.kind === 'error' && (
          <div
            data-testid="argomento-error"
            role="alert"
            style={{
              padding: '24px 16px',
              textAlign: 'center',
              fontFamily: t.fonts.body,
              fontSize: 14,
              color: t.text,
              lineHeight: 1.5,
            }}
          >
            {phase.message}
          </div>
        )}

        {phase.kind === 'ready' && (
          <ReadyBody
            data={phase.data}
            lessonProgress={phase.lessonProgress}
            lessonReadByMicroTopic={lessonReadByMicroTopic}
            onOpenLesson={(lessonId) => navigate(guidedLessonRoute(phase.data.argomentoId, lessonId))}
          />
        )}
      </main>
    </Screen>
  );
}

function ReadyBody({
  data,
  lessonProgress,
  lessonReadByMicroTopic,
  onOpenLesson,
}: {
  data: ArgomentoDetailResponse;
  lessonProgress: CanonicalLessonProgressSummary | null;
  lessonReadByMicroTopic: Record<string, LessonReadRecord>;
  onOpenLesson: (lessonId: string) => void;
}): JSX.Element {
  const t = useTokens();
  const lessonCount = data.microTopics.length;
  const canonicalArgomento = canonicalArgomentoFor(lessonProgress, data.argomentoId);
  const completionAvailable = canonicalArgomento !== null;
  const completedLessonCount = canonicalArgomento?.completedLessons ?? null;
  const totalLessonCount = canonicalArgomento?.totalLessons ?? lessonCount;
  const completionPct = canonicalArgomento?.completionPct ?? null;
  const ringPct = completionPct === null ? null : Math.max(0, Math.min(100, completionPct));
  const lessonRows = buildCanonicalLessonRows({
    argomentoId: data.argomentoId,
    microTopics: data.microTopics,
    readByMicroTopic: lessonReadByMicroTopic,
    guidedCompleted: false,
    guidedOpened: false,
    progressLessons: lessonProgress?.lessons ?? [],
  });

  return (
    <>
      <section
        data-testid="argomento-hero"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          background: t.surface,
          border: `1px solid ${t.line}`,
          borderRadius: t.radius.lg,
          padding: '16px 16px',
        }}
      >
        <div
          role="img"
          aria-label={completionAvailable
            ? `Percorso completato ${completionPct}%. ${canonicalHeroEvidenceText(canonicalArgomento)}`
            : 'Percorso completato non disponibile.'}
          data-testid="argomento-hero-ring"
          data-ring-tone={completionAvailable ? 'completion' : 'completion-unavailable'}
          style={{
            width: 64,
            minWidth: 64,
            height: 64,
            borderRadius: '50%',
            background: ringPct === null
              ? t.surface2
              : `conic-gradient(${t.accent} ${ringPct}%, ${t.line} ${ringPct}%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <span
            style={{
              position: 'absolute',
              background: t.surface,
              width: 50,
              height: 50,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: t.fonts.body,
              fontSize: 14,
              fontWeight: 700,
              color: t.text,
            }}
          >
            {completionPct === null ? '—' : `${completionPct}%`}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            data-testid="argomento-hero-band"
            style={{
              fontFamily: t.fonts.body,
              fontSize: 14,
              fontWeight: 700,
              color: t.accentInk,
              lineHeight: 1.3,
            }}
          >
            {completionAvailable ? 'Percorso completato' : 'Percorso completato non disponibile'}
          </div>
          <div
            data-testid="argomento-hero-evidence"
            style={{
              fontFamily: t.fonts.body,
              fontSize: 12.5,
              color: t.textMuted,
              lineHeight: 1.4,
              marginTop: 3,
            }}
          >
            {completionAvailable
              ? canonicalHeroEvidenceText(canonicalArgomento)
              : 'Il dato sul completamento non è disponibile.'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            <Chip>{`${totalLessonCount} ${totalLessonCount === 1 ? 'lezione' : 'lezioni'}`}</Chip>
            {completedLessonCount !== null && <Chip>{completedLessonsChip(completedLessonCount)}</Chip>}
          </div>

          {/* V29 S5: the 'Lettura: X di Y capitoli' block was shipped dead code
              — its state was hardcoded to 'none' so it could never render.
              Deleted with its constant (audit B7). */}
        </div>
      </section>

      <section>
        <div
          style={{
            fontFamily: t.fonts.mono,
            fontSize: 10,
            letterSpacing: 0.7,
            color: t.textFaint,
            textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          {`Lezioni · ${lessonCount}`}
        </div>

        {lessonCount === 0 ? (
          <div
            style={{
              padding: '20px 16px',
              textAlign: 'center',
              fontFamily: t.fonts.body,
              fontSize: 13,
              color: t.textMuted,
              lineHeight: 1.45,
              background: t.surface,
              border: `1px solid ${t.line}`,
              borderRadius: t.radius.lg,
            }}
          >
            Per questo argomento non sono ancora state preparate le lezioni.
          </div>
        ) : (
          <ul
            data-testid="sotto-temi-list"
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              background: t.surface,
              border: `1px solid ${t.line}`,
              borderRadius: t.radius.lg,
              overflow: 'hidden',
            }}
          >
            {lessonRows.map((lesson, i) => (
              <LessonRow
                key={lesson.lessonId}
                index={lesson.order || i + 1}
                name={lesson.title}
                state={lesson.state}
                statusAvailable={lessonProgress !== null}
                showTopBorder={i > 0}
                onOpen={() => onOpenLesson(lesson.lessonId)}
              />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function LessonRow({
  index,
  name,
  state,
  statusAvailable,
  showTopBorder,
  onOpen,
}: {
  index: number;
  name: string;
  state: 'not_started' | 'in_progress' | 'completed';
  statusAvailable: boolean;
  showTopBorder: boolean;
  onOpen: () => void;
}): JSX.Element {
  const t = useTokens();
  // V29 S5 (founder R1-8/R1-6): 'Ripassa' → 'Rileggi' — the completed CTA
  // reopens the READ lesson; 'Ripassa' stays reserved for practice surfaces.
  // Completed rows go NEUTRAL (Letta-style); the colored primary belongs to
  // the actionable rows (Riprendi/Inizia).
  const ctaLabel = !statusAvailable
    ? 'Apri'
    : state === 'completed' ? 'Rileggi' : state === 'in_progress' ? 'Riprendi' : 'Inizia';
  const stateLabel = !statusAvailable
    ? 'Stato non disponibile'
    : state === 'in_progress' ? 'In corso' : state === 'completed' ? 'Completata' : 'Da iniziare';
  const toneBg = statusAvailable && state === 'completed'
    ? t.rightSoft
    : statusAvailable && state === 'in_progress' ? t.accentSoft : t.surface2;
  const toneText = statusAvailable && state === 'completed'
    ? t.rightText
    : statusAvailable && state === 'in_progress' ? t.accentInk : t.textMuted;
  // Status rail — color is never the only signal (stateLabel accompanies it).
  const railColor = !statusAvailable
    ? t.line
    : state === 'completed' ? t.right : state === 'in_progress' ? t.accent : t.lineStrong;

  return (
    <li style={{ listStyle: 'none' }}>
      <div
        style={{
          borderTop: showTopBorder ? `1px solid ${t.line}` : 'none',
          borderLeft: `4px solid ${railColor}`,
          padding: '12px 14px 12px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <button
          onClick={onOpen}
          aria-label={`${ctaLabel}: ${name}. ${stateLabel}.`}
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            padding: 0,
            minHeight: 44,
            cursor: 'pointer',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            color: t.text,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 26,
              minWidth: 26,
              height: 26,
              borderRadius: 999,
              background: toneBg,
              color: toneText,
              border: `1px solid ${toneBg}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: t.fonts.body,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {index}
          </span>

          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontFamily: t.fonts.body,
                fontSize: 14,
                fontWeight: 700,
                color: t.text,
                lineHeight: 1.3,
              }}
            >
              {name}
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: t.fonts.body,
                fontSize: 12,
                fontWeight: 600,
                color: t.textMuted,
                marginTop: 2,
              }}
            >
              {stateLabel}
            </span>
          </span>

          <span
            data-testid="sotto-tema-lesson-cta"
            style={{
              flex: '0 0 auto',
              minHeight: 32,
              padding: '0 14px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: t.radius.pill,
              fontFamily: t.fonts.body,
              fontSize: 13,
              fontWeight: 700,
              // V29 S5: neutral pill on completed rows (Rileggi); the brand
              // color marks the actionable next step only.
              background: statusAvailable && state === 'completed' ? t.surface : t.accent,
              color: statusAvailable && state === 'completed' ? t.textMuted : '#fff',
              border: statusAvailable && state === 'completed' ? `1px solid ${t.lineStrong}` : 'none',
              pointerEvents: 'none',
            }}
          >
            {ctaLabel}
          </span>
          {/* Chevron — the whole ROW is the tap target; make that legible. */}
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke={t.textFaint} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true" style={{ flexShrink: 0 }}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

      </div>
    </li>
  );
}

function Chip({ children }: { children: string }): JSX.Element {
  const t = useTokens();
  return (
    <span
      style={{
        fontFamily: t.fonts.body,
        fontSize: 11.5,
        fontWeight: 600,
        color: t.textMuted,
        background: t.surface2,
        border: `1px solid ${t.line}`,
        borderRadius: t.radius.pill,
        padding: '3px 10px',
      }}
    >
      {children}
    </span>
  );
}

function canonicalArgomentoFor(
  summary: CanonicalLessonProgressSummary | null,
  argomentoId: string,
): CanonicalLessonProgressArgomento | null {
  return summary?.argomenti.find((arg) => arg.argomentoId === argomentoId) ?? null;
}

function canonicalHeroEvidenceText(arg: CanonicalLessonProgressArgomento): string {
  const completed = arg.completedLessons;
  const total = arg.totalLessons;
  return `${completed} ${completed === 1 ? 'lezione' : 'lezioni'} su ${total}.`;
}

function completedLessonsChip(count: number): string {
  return `${count} ${count === 1 ? 'completata' : 'completate'}`;
}
